import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { HandshakeIcon, SpinnerGapIcon } from "@phosphor-icons/react";
import { fetchCto } from "../lib/api";
import { useSeo } from "../lib/useSeo";
import { fmtUsd, fmtAge, fmtPct, changeColor } from "../lib/format";

const Row = ({ coin, onClick }) => {
  const ch24 = coin.priceChange?.h24 ?? 0;
  return (
    <button
      data-testid={`cto-row-${coin.symbol}`}
      onClick={onClick}
      className="w-full text-left border border-white/10 bg-card hover:border-neon/40 hover:bg-white/[0.03] transition-colors p-4 flex items-center gap-4"
    >
      {coin.image ? (
        <img src={coin.image} alt={coin.symbol} className="w-11 h-11 object-cover border border-white/10 shrink-0" onError={(e) => (e.target.style.display = "none")} />
      ) : (
        <div className="w-11 h-11 flex items-center justify-center bg-white/5 border border-white/10 font-mono text-xs shrink-0">{(coin.symbol || "?").slice(0, 3)}</div>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-heading font-bold truncate">{coin.symbol}</span>
          <span className="text-xs text-muted-foreground truncate">{coin.name}</span>
        </div>
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {(coin.cto?.signals || []).slice(0, 3).map((s, i) => (
            <span key={i} className="text-[10px] font-mono px-1.5 py-0.5 border border-white/12 text-muted-foreground">{s}</span>
          ))}
        </div>
      </div>
      <div className="text-right shrink-0 hidden sm:block">
        <div className="font-mono text-sm">{fmtUsd(coin.marketCap)}</div>
        <div className="text-[11px] font-mono text-muted-foreground">mcap · {fmtAge(coin.ageHours)}</div>
        {coin.cto?.since?.multiple != null && (
          <div className="text-[11px] font-mono mt-0.5" style={{ color: coin.cto.since.multiple >= 1 ? "#00E599" : "#FF3366" }}>
            {coin.cto.since.multiple >= 1 ? `▲ ${coin.cto.since.multiple}× since flagged` : `▼ ${coin.cto.since.pct}%`}
          </div>
        )}
      </div>
      <div className="text-right shrink-0 w-20">
        <div className="font-mono text-sm" style={{ color: changeColor(ch24) }}>{fmtPct(ch24)}</div>
        <div className="text-[11px] font-mono text-muted-foreground">24h · {fmtUsd(coin.liquidity)} liq</div>
      </div>
    </button>
  );
};

export default function CtoRadar() {
  useSeo({ title: "CTO Radar · AlphaApeing", description: "Tokens undergoing a Community Take Over — the dev stepped back and the community is running it. Confirmed and possible CTOs, with the on-chain signals behind each." });
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({ queryKey: ["cto"], queryFn: fetchCto, refetchInterval: 60000 });

  const confirmed = data?.confirmed || [];
  const possible = data?.possible || [];

  return (
    <div className="max-w-[1000px] mx-auto px-4 sm:px-6 py-6 space-y-8 animate-fade-up">
      <div>
        <div className="flex items-center gap-2.5">
          <HandshakeIcon size={30} weight="fill" className="text-neon" />
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter leading-none">CTO RADAR</h1>
        </div>
        <p className="text-muted-foreground mt-3 text-sm leading-relaxed max-w-2xl">
          <strong className="text-white">Community Take Over</strong> — when the original dev walks away and the community picks the token up.
          We flag <span className="text-neon">confirmed</span> CTOs (self-labeled or verified by us) and <span className="text-muted-foreground">possible</span> ones
          (dumped-then-revived survivors). Signals & evidence, not predictions — CTOs can rip or fade fast. DYOR.
        </p>
      </div>

      {isLoading ? (
        <div className="flex flex-col items-center justify-center gap-3 py-20 text-muted-foreground font-mono text-sm">
          <SpinnerGapIcon size={26} className="animate-spin text-neon" /> Scanning for takeovers…
        </div>
      ) : (
        <>
          <section data-testid="cto-confirmed">
            <h2 className="font-heading font-bold text-lg mb-3 flex items-center gap-2">
              <span className="text-neon">Confirmed</span>
              <span className="text-xs font-mono text-muted-foreground">{confirmed.length}</span>
            </h2>
            {confirmed.length ? (
              <div className="space-y-2">
                {confirmed.map((c) => <Row key={c.address} coin={c} onClick={() => navigate(`/coin/${c.address}`)} />)}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground border border-dashed border-white/10 p-6 text-center">
                No confirmed community takeovers right now. Check the possible list below, or open a token and (as admin) mark it a CTO.
              </p>
            )}
          </section>

          <section data-testid="cto-possible">
            <h2 className="font-heading font-bold text-lg mb-3 flex items-center gap-2">
              Possible <span className="text-xs font-mono text-muted-foreground">{possible.length}</span>
            </h2>
            {possible.length ? (
              <div className="space-y-2">
                {possible.map((c) => <Row key={c.address} coin={c} onClick={() => navigate(`/coin/${c.address}`)} />)}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground border border-dashed border-white/10 p-6 text-center">
                Nothing showing the community-revival pattern right now.
              </p>
            )}
          </section>
        </>
      )}

      <p className="text-[11px] text-muted-foreground/60 leading-relaxed">
        A "possible" CTO is a heuristic (dumped hard, bounced back with buyers, still liquid, has community socials) — not proof the dev left. Not financial advice.
      </p>
    </div>
  );
}
